// 인증 관련 타입
export interface User {
  id: number;
  username?: string; // 백엔드와 호환성을 위한 필드
  email: string;
  name: string;
  role: 'ADMIN' | 'MANAGER' | 'STAFF';
  phone?: string;
  lastLogin?: string;
  isActive: boolean;
  storeId: number;
  status?: 'pending' | 'approved' | 'rejected';
  createdAt?: string;
  approvedAt?: string;
  approvedBy?: string;
  rejectionReason?: string;
  pushToken?: string;
  // 매장 관련 추가 필드
  storeName?: string;
  businessNumber?: string;
  storeAddress?: string;
  businessLicenseUrl?: string;
  // 백엔드 Store 모델과의 호환성을 위한 필드 (passwordHash 제거 - 보안상 위험)
}

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

// 매장 관련 타입
export interface StoreInfo {
  id: number;
  name: string;
  address: string;
  phone: string;
  // 백엔드 직렬화 기본(camelCase)와의 호환을 위한 선택 필드
  phoneNumber?: string;
  // 계정/점주 정보 (Identity 서비스 Store 응답과 호환)
  username?: string;
  email?: string;
  ownerName?: string;
  approvalStatus?: string; // pending | approved | rejected | suspended
  businessRegistrationNumber: string;
  operatingHours: {
    open: string;
    close: string;
  };
  logoUrl?: string;
  isActive: boolean;
  ownerId: number;
}

// 주문 관련 타입
export interface OrderItem {
  id?: number;
  menuItemId: number;
  name: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  options?: Array<OrderOption> | string;  // string 타입도 허용 (JSON 문자열)
  specialRequests?: string;
}

export interface OrderOption {
  name: string;
  quantity: number;
  price: number;
  // 배달의민족 주문인 경우, 이 옵션 가격이 메인 메뉴 가격에 이미 포함되어 있는지 여부
  includedInItemPrice?: boolean;
}

export enum OrderStatus {
  Received = 0,
  Accepted = 1,
  Preparing = 2,
  ReadyForDelivery = 3,
  OutForDelivery = 4,
  Delivered = 5,
  Completed = 6,
  Cancelled = 7,
  Rejected = 8,
}

// 브릿지앱 형식의 주문 타입
export interface BridgeOrder {
  orderId?: number;
  orderNumber: string;
  customerName: string;
  sourcePlatform?: string;
  menuSummary: string;
  totalAmount: number;
  orderTime: string;
  status?: number;
  
  // 추가 필드 (상세 정보 표시용)
  sourceOrderId?: string;
  customerPhone?: string;
  deliveryAddress?: string;
  customerRequest?: string;
  orderItems?: OrderItem[];
}

// 브릿지앱 API 응답 타입
export interface BridgeOrderResponse {
  orders: BridgeOrder[];
}

export interface Order {
  id: number;
  orderNumber: string;
  storeId: number;
  sourcePlatform: string;
  sourceOrderId: string;
  status: OrderStatus;
  totalAmount: number;
  deliveryFee: number;
  deliveryAddress: string;
  customerName: string;
  customerPhone: string;
  customerRequest: string;
  riderRequest?: string;
  orderTime: string;
  acceptedTime: string | null;
  orderItems: OrderItem[];
  notes?: string[];
  paymentMethod?: string;
  paymentStatus?: 'PAID' | 'PENDING' | 'FAILED';
}

// 재고 관련 타입
export interface IngredientCategory {
  id: number;
  name: string;
  description?: string;
}

export interface InventoryItem {
  id: string;
  name: string;
  category: string;
  quantity: number;
  reorderLevel: number;
  supplier: string;
  unitPrice: number;
  location: string;
  autoOrder?: boolean;
  autoOrderQuantity?: number;
}

export interface BatchInfo {
  id?: number;
  quantity: number;
  actualVolume?: number | null; // ✅ Phase 1: 배치별 실제 용량 (optional, 향후 개선용)
  expiryDate?: string;
  skipExpiryDate: boolean;
}

export interface Ingredient {
  id: number;
  name: string;
  brand?: string;
  volume: number;
  quantity?: number;
  unit: string;
  currentQuantity: number;
  currentQuantityUnit?: string; // 현 수량의 단위
  currentVolume?: number; // 현 용량
  volumeUnit?: string; // 용량 단위
  supplierId: number;
  batches?: BatchInfo[]; // 배치 정보 추가
  orderThreshold: number;
  inboundRequestQuantity: number; // 입고 요청 수량 (재고 부족시 발주할 수량)
  supplierName: string;
  storeId: number;
  createdAt: string;
  currentStock: number;  // 현재 재고 수량
  minimumStock: number;  // 최소 재고 수량
  expiryDate?: string;   // 소비기한
  skipExpiryDate: boolean; // 소비기한 생략 여부
  brandName?: string;
  // VAT 관련 필드 (식재료별 부가세 설정)
  vatApplicable?: boolean; // 과세 여부
  unitPriceSource?: 'gross' | 'net'; // 입력 유형: "gross"(부가세 포함) 또는 "net"(부가세 제외)
  vatRate?: number; // 세율 (예: 0.1 = 10%)
}

// 포장용기 관련 타입 (식재료와 유사하지만 소비기한/용량 관련 필드 제외)
export interface PackagingContainer {
  id: number;
  name: string;
  brand?: string;
  currentQuantity: number;
  currentQuantityUnit?: string; // 현 수량의 단위
  unit: string;
  orderThreshold: number;
  // VAT 관련 필드 (식재료와 동일한 구조)
  vatApplicable?: boolean; // 과세 여부
  unitPriceSource?: 'gross' | 'net'; // 입력 유형: "gross"(부가세 포함) 또는 "net"(부가세 제외)
  inboundRequestQuantity: number; // 입고 요청 수량 (재고 부족시 발주할 수량)
  supplierId: number;
  supplierName: string;
  storeId: number;
  createdAt: string;
  updatedAt?: string;
}

export interface StockAdjustment {
  id: number;
  ingredientId: number;
  ingredientName: string;
  quantity: number;
  previousStock: number;
  newStock: number;
  reason: string;
  adjustedBy: number; // userId
  adjustedByName: string;
  timestamp: string;
}

// 입출고 기록 관련 타입
export interface StockTransaction {
  id: number;
  ingredientId: number;
  ingredientName: string;
  type: 'IN' | 'OUT'; // 입고, 출고
  quantity: number;
  previousQuantity: number;
  newQuantity: number;
  volumeQuantity?: number; // 용량 정보 (시스템 자동 차감 시)
  volumeUnit?: string; // 용량 단위
  reason: string;
  createdBy: string; // '관리자' 또는 '시스템'
  createdAt: string;
  storeId: number;
  expiryDate?: string; // 소비기한 (입고 시에만 사용)
  skipExpiryDate: boolean; // 소비기한 생략 여부
  batchNumber?: string; // 배치 번호
  remainingQuantity?: number; // 남은 수량
  status?: string; // 배치 상태
}

// 포장용기 입출고 기록 (StockTransaction과 유사하지만 배치/소비기한 필드 제외)
export interface PackagingContainerTransaction {
  id: number;
  packagingContainerId: number;
  packagingContainerName: string;
  type: 'IN' | 'OUT'; // 입고, 출고
  quantity: number;
  previousQuantity: number;
  newQuantity: number;
  reason: string;
  createdBy: string; // '관리자' 또는 '시스템'
  createdAt: string;
  storeId: number;
  unitPrice?: number; // 단가 (입고 시에만 사용)
  totalPrice?: number; // 총액 (입고 시에만 사용)
  unitPriceNet?: number; // 부가세 제외 단가
  vatApplicable?: boolean;
  unitPriceSource?: string;
  vatRate?: number;
  batches?: PackagingContainerTransactionBatch[]; // 배치 정보
}

// 포장용기 배치 정보 (소비기한 필드 제외)
export interface PackagingContainerTransactionBatch {
  id: number;
  packagingContainerTransactionId: number;
  packagingContainerId: number;
  batchNumber: string;
  quantity: number;
  remainingQuantity: number;
  type: 'IN' | 'OUT';
  status: 'ACTIVE' | 'CONSUMED';
  consumedFromBatchId?: number;
  createdAt: string;
  storeId: number;
}

// 포장용기 배치 정보 DTO (입고 시 사용)
export interface PackagingContainerBatchInfo {
  quantity: number;
}

export interface StockTransactionRequest {
  ingredientId: number;
  type: 'IN' | 'OUT';
  quantity: number;
  reason: string;
  expiryDate?: string;
  skipExpiryDate: boolean;
}

// 배치별 소비기한 관리 타입
export interface StockTransactionBatch {
  id: number;
  stockTransactionId: number;
  ingredientId: number;
  batchNumber: string;
  quantity: number;
  remainingQuantity: number;
  expiryDate?: string;
  skipExpiryDate: boolean;
  type: 'IN' | 'OUT';
  consumedFromBatchId?: number;
  status: 'ACTIVE' | 'CONSUMED' | 'EXPIRED';
  createdAt: string;
  storeId: number;
}

export interface BatchTransactionRequest {
  ingredientId: number;
  type: 'IN' | 'OUT';
  batches: BatchInfo[]; // 여러 배치 지원
  reason: string;
}

export interface ExpiredBatchInfo {
  ingredientId: number;
  ingredientName: string;
  batchNumber: string;
  remainingQuantity: number;
  expiryDate: string;
  daysOverdue: number;
}

export interface MenuItem {
  id: number;
  storeId: number;
  name: string;
  description?: string;
  category: string;
  cost?: number;
  ingredients: Array<{
    ingredientId: number;
    name: string;
    quantity: number;
  }>;
  imageUrl?: string;
  isActive: boolean;
  inStock: number;
  preparationTime?: number;
  createdAt?: string;
  updatedAt?: string;
}

// 공급망 관련 타입
export enum PurchaseOrderStatus {
  Draft = 'DRAFT',
  Submitted = 'SUBMITTED',
  Confirmed = 'CONFIRMED',
  Shipped = 'SHIPPED',
  Received = 'RECEIVED',
  Cancelled = 'CANCELLED',
  Rejected = 'REJECTED'
}

export interface SupplierProduct {
  id: number;
  name: string;
  price: number;
  unit: string;
  minimumOrderQuantity?: number;
  ingredientId?: number;
}

export interface Supplier {
  id: number;
  name: string;
  contactPerson: string;
  phoneNumber: string;
  email: string;
  address: string;
  storeId: number;
  createdAt: string;
  updatedAt?: string;
  orderThreshold: number; // 발주 기준 수량
  orderDeadlineHour: number; // 발주 마감 시간 (시)
  orderDeadlineMinute: number; // 발주 마감 시간 (분)
}

export interface PurchaseOrderItem {
  id: number;
  productId: number;
  productName: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  totalPrice: number;
  receivedQuantity?: number;
  ingredientId?: number;
}

export interface PurchaseOrder {
  id: number;
  storeId: number;
  orderNumber: string;
  supplierId: number;
  supplierName: string;
  status: PurchaseOrderStatus;
  createdAt: string;
  updatedAt: string;
  estimatedDeliveryDate?: string;
  actualDeliveryDate?: string;
  totalAmount: number;
  note?: string;
  createdBy: number; // userId
  items: PurchaseOrderItem[];
}

// 분석 관련 타입
export interface StoreMetrics {
  date: string;
  totalOrders: number;
  totalSales: number;
  averageOrderValue: number;
  itemsSold: number;
}

export interface InventoryPrediction {
  ingredientId: number;
  ingredientName: string;
  currentStock: number;
  minimumStock: number;
  estimatedDaysRemaining: number;
  suggestedOrderQuantity: number;
}

export interface SalesInsight {
  id: number;
  title: string;
  description: string;
  type: 'POSITIVE' | 'NEGATIVE' | 'NEUTRAL' | 'WARNING';
  createdAt: string;
  metric?: number;
  metricChange?: number;
}

export interface ChartData {
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string | string[];
    fill?: boolean;
  }>;
}

export interface TopSellingItem {
  menuItemId: number;
  name: string;
  quantity: number;
  totalSales: number;
  percentageOfTotalSales: number;
}

export interface CustomerAnalytics {
  totalCustomers: number;
  newCustomers: number;
  returningCustomers: number;
  averageOrdersPerCustomer: number;
  topPlatforms: Array<{
    platform: string;
    orders: number;
    percentage: number;
  }>;
}

// 메뉴 관련 타입 정의
export interface MenuIngredient {
  id?: number;
  name: string;
  quantity: number;
  unit: string;
  ingredientId?: number;
}

export interface MenuPackagingContainer {
  id?: number;
  packagingContainerId: number;
  name: string;
  quantity: number;
  unit: string;
}

export interface MenuWorkStation {
  id?: number;
  name: string;
}

export interface Menu {
  id: number;
  name: string;
  menuType: string;
  isActive: boolean;
  description?: string;
  keywords?: string;
  createdAt: string;
  updatedAt: string;
  packagingSkipped: boolean;
  ingredients: MenuIngredient[];
  packagingContainers: MenuPackagingContainer[];
  workStations: MenuWorkStation[];
  cookingMethods?: CookingMethod[];
}

export interface MenuListResponse {
  menus: Menu[];
  totalCount: number;
}

export interface MenuCreateUpdate {
  name: string;
  menuType: string;
  isActive: boolean;
  keywords?: string;
  packagingSkipped: boolean;
  ingredients: MenuIngredientCreate[];
  packagingContainers: MenuPackagingContainerCreate[];
  workStations?: MenuWorkStationCreate[];
  cookingMethods: CookingMethodRequest[];
  storeId?: number;
  price: number;
}

export interface MenuIngredientCreate {
  name: string;
  quantity: number;
  unit: string;
  ingredientId?: number;
}

export interface MenuPackagingContainerCreate {
  packagingContainerId: number;
  name: string;
  quantity: number;
  unit: string;
}

export interface MenuWorkStationCreate {
  name: string;
}

export interface MenuStatusUpdate {
  isActive: boolean;
}

// 작업관리 관련 타입
export interface CookingMethod {
  id: number;
  ingredientId: number;
  ingredientName: string;
  workStationId?: number; // 작업대 ID
  workStationName?: string; // 작업대 이름
  cookingTimeMinutes: number;
  cookingTimeSeconds: number;
  method: string;
}

export interface CookingMethodRequest {
  ingredientId: number;
  ingredientName: string;
  workStationId?: number; // 작업대 ID
  workStationName?: string; // 작업대 이름
  cookingTimeMinutes: number;
  cookingTimeSeconds: number;
  method: string;
}

export interface WorkStation {
  id: number;
  name: string;
  brand: string;
  serviceContact: string;
  cookingMethods: CookingMethod[];
  storeId: number;
  createdAt: string;
  updatedAt?: string;
}

// 발주서 관련 타입 (새로운 시스템)
export enum NewPurchaseOrderStatus {
  Pending = 0,        // 대기중
  Ordered = 1,        // 발주완료
  InboundCompleted = 2, // 입고완료
  PartialInbound = 3, // 일부 입고완료 (실제 입고 수량이 0인 항목이 있는 경우)
  AllMissing = 4       // 입고누락 (모든 항목의 실제 입고 수량이 0인 경우)
}

export interface NewPurchaseOrder {
  id: number;
  storeId: number;
  supplierId: number;
  supplierName: string;
  orderNumber: string;
  orderDate: string;
  status: NewPurchaseOrderStatus;
  inboundCompletedAt?: string;
  notes?: string;
  receiptFilePath?: string;
  createdAt: string;
  updatedAt: string;
  items: NewPurchaseOrderItem[];
}

export interface NewPurchaseOrderItem {
  id: number;
  purchaseOrderId: number;
  ingredientId: number;
  ingredientName: string;
  packagingContainerId?: number; // 포장용기 ID (선택적)
  requestedQuantity: number;
  actualQuantity?: number;
  unit: string;
  createdAt: string;
}

export interface CreatePurchaseOrderRequest {
  storeId: number;
  supplierId: number;
  supplierName: string;
  items: CreatePurchaseOrderItemRequest[];
  notes?: string;
}

export interface CreatePurchaseOrderItemRequest {
  ingredientId: number;
  ingredientName: string;
  packagingContainerId?: number; // 포장용기 ID (선택적)
  requestedQuantity: number;
  requestedVolume: number; // 요청 용량
  unit: string;
  volumeUnit: string; // 용량 단위
}

export interface ProcessInboundRequest {
  items: ProcessInboundItemRequest[];
  notes?: string;
  receiptFilePath?: string;
}

export interface ProcessInboundItemRequest {
  ingredientId: number;
  packagingContainerId?: number; // 포장용기 ID (선택적)
  actualQuantity: number;
  actualVolume?: number | null; // ✅ Phase 1: 실제 입고 용량 (nullable)
  batches: Array<{
    quantity: number;
    expiryDate?: string;
    skipExpiryDate?: boolean;
  }>;
  packagingContainerBatches?: Array<{
    quantity: number;
  }>; // ✅ 포장용기 배치 정보 (소비기한 없음)
  unitPrice?: number; // 단가
  totalPrice?: number; // 총액
}

export interface CanOrderResult {
  canOrder: boolean;
  totalRequestQuantity: number;
  orderThreshold: number;
  message: string;
}

export interface OrderItemInfo {
  ingredientId: number;
  ingredientName: string;
  currentQuantity: number;
  orderThreshold: number;
  requestedQuantity: number;
  unit: string;
  packagingContainerId?: number; // 포장용기 ID (선택적)
}

// 폐기 분석 관련 타입
export interface WasteAnalytics {
  totalCost: number;
  totalCount: number;
  wasteRate: number;
  totalInboundQuantity?: number;
}

export interface WasteMonthlyTrend {
  month: number;
  cost: number;
  count: number;
}

export interface WasteTopIngredient {
  ingredientId: number;
  ingredientName: string;
  cost?: number;
  count?: number;
}

// 대량 업로드 관련 타입
export interface SheetSummary {
  total: number;
  success: number;
  failed: number;
}

export interface BulkUploadSummary {
  suppliers: SheetSummary;
  workStations: SheetSummary;
  ingredients: SheetSummary;
  packagingContainers: SheetSummary;
  menus: SheetSummary;
}

export interface BulkUploadResult {
  rowNumber: number;
  itemName: string;
  status: 'success' | 'failed';
  itemId?: number;
  error?: string;
}

export interface BulkUploadResults {
  suppliers: BulkUploadResult[];
  workStations: BulkUploadResult[];
  ingredients: BulkUploadResult[];
  packagingContainers: BulkUploadResult[];
  menus: BulkUploadResult[];
}

export interface BulkUploadResponse {
  success: boolean;
  summary: BulkUploadSummary;
  results: BulkUploadResults;
} 